import { FRAME_RATE } from "./constants.js";
import { InputState } from "./input.js";

const ACTION_DEFS = [
  { name: "left", aliases: ["ArrowLeft", "KeyA"], labels: ["left", "a"] },
  { name: "right", aliases: ["ArrowRight", "KeyD"], labels: ["right", "d"] },
  { name: "up", aliases: ["ArrowUp", "KeyW", "Space"], labels: ["up", "w", "space", "jump"] },
  { name: "pause", aliases: ["KeyP"], labels: ["pause", "p"] },
  { name: "respawn", aliases: ["KeyF"], labels: ["respawn", "f"] },
  { name: "escape", aliases: ["Escape"], labels: ["escape", "esc"] },
  { name: "yes", aliases: ["KeyY"], labels: ["yes", "y"] },
  { name: "no", aliases: ["KeyN"], labels: ["no", "n"] },
];

const ACTION_NAMES = ACTION_DEFS.map(({ name }) => name);
const CODE_TO_ACTION = new Map();
const LABEL_TO_ACTION = new Map();
const ACTION_TO_ALIASES = new Map();

for (const definition of ACTION_DEFS) {
  ACTION_TO_ALIASES.set(definition.name, definition.aliases);
  for (const alias of definition.aliases) {
    CODE_TO_ACTION.set(alias, definition.name);
  }
  for (const label of definition.labels) {
    LABEL_TO_ACTION.set(label, definition.name);
  }
}

LABEL_TO_ACTION.delete("down");
LABEL_TO_ACTION.delete("s");

function formatFrame(frame) {
  const seconds = Math.floor(frame / FRAME_RATE);
  const subFrame = frame % FRAME_RATE;
  return `${seconds}:${subFrame}`;
}

function formatLine(frame, type, action) {
  return `${formatFrame(frame)} ${type} ${action}`;
}

function parseScriptLine(line) {
  const match = line.match(/^(\d+):(\d+)\s+(press|rel)\s+([a-z]+)$/i);
  if (!match) {
    return null;
  }
  const seconds = Number(match[1]);
  const subFrame = Number(match[2]);
  const type = match[3].toLowerCase();
  const action = LABEL_TO_ACTION.get(match[4].toLowerCase());
  if (!Number.isInteger(seconds) || !Number.isInteger(subFrame) || !action) {
    return null;
  }
  return {
    frame: seconds * FRAME_RATE + subFrame,
    type,
    action,
  };
}

function normalizeScriptText(value) {
  return String(value ?? "").replace(/\r\n/g, "\n");
}

function normalizeBotEvents(events) {
  return [...(events ?? [])]
    .filter((event) => event && Number.isInteger(event.frame) && LABEL_TO_ACTION.has(event.action) && (event.type === "press" || event.type === "rel"))
    .map((event, index) => ({
      frame: event.frame,
      type: event.type,
      action: event.action,
      order: event.order ?? index,
    }))
    .sort((left, right) => {
      if (left.frame !== right.frame) {
        return left.frame - right.frame;
      }
      return left.order - right.order;
    });
}

export class ScriptedInputState {
  constructor(target = window) {
    this.raw = new InputState(target);
    this.recordField = null;
    this.scriptField = null;
    this.botEventsByFrame = new Map();
    this.scriptedDown = new Set();
    this.effectiveDown = new Set();
    this.manualAliasDown = new Set();
    this.botPressedActions = new Set();
    this.recordedLines = [];
    this.botScriptText = "";
    this.currentFrame = 0;
    this.lastSyncedFrame = null;
    this.active = false;
    this.handleScriptFieldInput = null;
  }

  attachPanels({ recordField, scriptField, initialScript = "", onScriptChange = null }) {
    this.recordField = recordField;
    this.scriptField = scriptField;
    this.onScriptChange = onScriptChange;
    this.setBotScript(initialScript, { triggerChange: false, replayBeforeFrame: 0 });
    this.renderRecordedLines();
    this.scriptField?.removeEventListener("input", this.handleScriptFieldInput);
    this.handleScriptFieldInput = () => {
      this.setBotScript(this.scriptField.value, {
        triggerChange: true,
        replayBeforeFrame: this.active ? this.currentFrame : 0,
      });
    };
    this.scriptField?.addEventListener("input", this.handleScriptFieldInput);
  }

  getBotScript() {
    return this.botScriptText;
  }

  setBotScript(value, { triggerChange = true, replayBeforeFrame = null } = {}) {
    const nextValue = normalizeScriptText(value);
    this.botScriptText = nextValue;
    if (this.scriptField) {
      this.scriptField.value = nextValue;
    }
    if (triggerChange) {
      this.onScriptChange?.(nextValue);
    }
    const events = [];
    for (const line of nextValue.split(/\n/)) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }
      const parsed = parseScriptLine(trimmed);
      if (!parsed) {
        continue;
      }
      events.push({
        ...parsed,
        order: events.length,
      });
    }
    this.parseBotEvents(replayBeforeFrame ?? (this.active ? this.currentFrame : 0), events);
  }

  updateScriptFieldValue(value, { triggerChange = true } = {}) {
    const nextValue = normalizeScriptText(value);
    this.botScriptText = nextValue;
    if (this.scriptField) {
      this.scriptField.value = nextValue;
    }
    if (triggerChange) {
      this.onScriptChange?.(nextValue);
    }
  }

  setBotEvents(events, { triggerChange = true, replayBeforeFrame = null, scriptText = null } = {}) {
    const nextValue = scriptText == null ? this.botScriptText : normalizeScriptText(scriptText);
    this.botScriptText = nextValue;
    if (this.scriptField) {
      this.scriptField.value = nextValue;
    }
    if (triggerChange) {
      this.onScriptChange?.(nextValue);
    }
    this.parseBotEvents(replayBeforeFrame ?? (this.active ? this.currentFrame : 0), events);
  }

  resetRace() {
    this.active = true;
    this.currentFrame = 0;
    this.recordedLines = [];
    this.manualAliasDown = new Set(this.raw.down);
    this.scriptedDown.clear();
    this.effectiveDown.clear();
    this.botPressedActions.clear();
    this.lastSyncedFrame = null;
    this.parseBotScript(0);
    this.renderRecordedLines();
  }

  clearRace() {
    this.active = false;
    this.currentFrame = 0;
    this.recordedLines = [];
    this.scriptedDown.clear();
    this.effectiveDown.clear();
    this.botPressedActions.clear();
    this.lastSyncedFrame = null;
    this.renderRecordedLines();
  }

  parseBotScript(replayBeforeFrame = 0, scriptText = null) {
    const events = [];
    const lines = normalizeScriptText(scriptText ?? this.botScriptText).split(/\n/);
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }
      const parsed = parseScriptLine(trimmed);
      if (!parsed) {
        continue;
      }
      events.push({
        ...parsed,
        order: events.length,
      });
    }
    this.parseBotEvents(replayBeforeFrame, events);
  }

  parseBotEvents(replayBeforeFrame = 0, events = []) {
    const eventsByFrame = new Map();
    const scriptedDown = new Set();
    for (const event of normalizeBotEvents(events)) {
      if (event.frame < replayBeforeFrame) {
        if (event.type === "press") {
          scriptedDown.add(event.action);
        } else {
          scriptedDown.delete(event.action);
        }
      }
      if (!eventsByFrame.has(event.frame)) {
        eventsByFrame.set(event.frame, []);
      }
      eventsByFrame.get(event.frame).push(event);
    }
    this.botEventsByFrame = eventsByFrame;
    this.scriptedDown = scriptedDown;
  }

  syncFrame(frame) {
    if (!this.active) {
      return;
    }
    if (this.lastSyncedFrame === frame) {
      return;
    }
    this.currentFrame = frame;
    this.lastSyncedFrame = frame;
    this.botPressedActions.clear();

    this.reconcileState(frame);

    for (const event of this.botEventsByFrame.get(frame) ?? []) {
      this.applyBotEvent(event, frame);
    }

    for (const transition of this.raw.transitions) {
      this.applyManualTransition(transition, frame);
    }

    this.reconcileState(frame);
  }

  beginFrame() {
    this.botPressedActions.clear();
    this.raw.beginFrame();
  }

  isDown(code) {
    const action = CODE_TO_ACTION.get(code);
    if (!action) {
      return this.raw.isDown(code);
    }
    return this.effectiveDown.has(action);
  }

  isManualDown(code) {
    return this.raw.isDown(code);
  }

  isScriptedDown(code) {
    const action = CODE_TO_ACTION.get(code);
    if (!action) {
      return false;
    }
    return this.scriptedDown.has(action);
  }

  wasPressed(code) {
    const action = CODE_TO_ACTION.get(code);
    if (!action) {
      return this.raw.wasPressed(code);
    }
    return this.botPressedActions.has(action) || ACTION_TO_ALIASES.get(action).some((alias) => this.raw.wasPressed(alias));
  }

  isActionDown(action) {
    return this.scriptedDown.has(action) || ACTION_TO_ALIASES.get(action).some((alias) => this.manualAliasDown.has(alias));
  }

  reconcileState(frame) {
    for (const action of ACTION_NAMES) {
      const isDown = this.isActionDown(action);
      const wasDown = this.effectiveDown.has(action);
      if (isDown === wasDown) {
        continue;
      }
      if (isDown) {
        this.effectiveDown.add(action);
        this.recordTransition(frame, "press", action);
      } else {
        this.effectiveDown.delete(action);
        this.recordTransition(frame, "rel", action);
      }
    }
  }

  applyBotEvent(event, frame) {
    const wasDown = this.isActionDown(event.action);
    if (event.type === "press") {
      this.scriptedDown.add(event.action);
      this.botPressedActions.add(event.action);
    } else {
      this.scriptedDown.delete(event.action);
    }
    const isDown = this.isActionDown(event.action);
    if (wasDown === isDown) {
      return;
    }
    if (isDown) {
      this.effectiveDown.add(event.action);
    } else {
      this.effectiveDown.delete(event.action);
    }
    this.recordTransition(frame, event.type, event.action);
  }

  applyManualTransition(transition, frame) {
    const action = CODE_TO_ACTION.get(transition.code);
    if (!action) {
      return;
    }
    const wasDown = this.isActionDown(action);
    if (transition.type === "press") {
      this.manualAliasDown.add(transition.code);
    } else {
      this.manualAliasDown.delete(transition.code);
    }
    const isDown = this.isActionDown(action);
    if (wasDown === isDown) {
      return;
    }
    if (isDown) {
      this.effectiveDown.add(action);
      this.recordTransition(frame, "press", action);
    } else {
      this.effectiveDown.delete(action);
      this.recordTransition(frame, "rel", action);
    }
  }

  recordTransition(frame, type, action) {
    this.recordedLines.push(formatLine(frame, type, action));
    this.renderRecordedLines();
  }

  renderRecordedLines() {
    if (this.recordField) {
      this.recordField.value = this.recordedLines.join("\n");
      this.recordField.scrollTop = this.recordField.scrollHeight;
    }
  }

  dispose() {
    this.scriptField?.removeEventListener("input", this.handleScriptFieldInput);
    this.handleScriptFieldInput = null;
    this.raw.dispose();
    this.recordField = null;
    this.scriptField = null;
    this.onScriptChange = null;
    this.botEventsByFrame.clear();
    this.scriptedDown.clear();
    this.effectiveDown.clear();
    this.manualAliasDown.clear();
    this.botPressedActions.clear();
    this.recordedLines = [];
    this.active = false;
  }
}
